import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listPeople } from '@/api/people';
import { getLocations } from '@/api/locations';

/**
 * Assignee pickers shared by the single-asset Assign dialog, the bulk-assign
 * dialog and the register's "assigned to" filter. Promoted out of the asset
 * detail page once the third caller appeared.
 *
 * Both take and return the id as a STRING, because that is what the surrounding
 * `<select>` / form state uses; an empty string means "nothing chosen".
 */

export const pickerInputCls = 'w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

/** Search-as-you-type person picker — sets the chosen PersonId (as a string). */
export function PersonPicker({
  value,
  onChange,
  label,
  placeholder = 'Search people by name or email',
  initialName,
}: {
  value: string;
  onChange: (v: string, displayName?: string) => void;
  /** Accessible name — distinguishes several pickers in one dialog. */
  label?: string;
  placeholder?: string;
  /** The name to show for a `value` that is already set when the picker opens (a person picked for the user). */
  initialName?: string;
}) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string | null>(initialName ?? null);

  const results = useQuery({
    queryKey: ['people-search', term],
    queryFn: ({ signal }) => listPeople({ search: term, pageSize: 8 }, signal),
    enabled: open && term.trim().length >= 2,
  });

  // Once a person is chosen the input is replaced by a chip, so the choice is
  // unambiguous and can be cleared in one click.
  if (value && chosen) {
    return (
      <div className="flex items-center gap-2">
        <span className="rounded bg-bg px-2 py-1 text-sm font-normal text-navy">{chosen}</span>
        <button
          type="button"
          onClick={() => { onChange(''); setChosen(null); }}
          className="text-xs font-normal text-red hover:underline"
        >
          Clear
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        placeholder={placeholder}
        aria-label={label ?? 'Person search'}
        className={pickerInputCls}
      />
      {open && results.data && results.data.Items.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded border border-border bg-white text-sm shadow">
          {results.data.Items.map((p) => (
            <li key={p.PersonId}>
              <button
                type="button"
                onClick={() => {
                  onChange(String(p.PersonId), p.DisplayName);
                  setChosen(p.DisplayName);
                  setOpen(false);
                  setTerm('');
                }}
                className="block w-full px-3 py-1.5 text-left font-normal hover:bg-bg"
              >
                {p.DisplayName} <span className="text-darkGrey/60">({p.PrimaryEmail})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Active-locations dropdown — sets the chosen LocationId (as a string). */
export function LocationSelect({
  value,
  onChange,
  label = 'Location',
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
}) {
  const locations = useQuery({
    queryKey: ['locations', 'active'],
    queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal),
    retry: false,
  });
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className={pickerInputCls}>
      <option value="">{locations.isLoading ? 'Loading locations…' : 'Select a location'}</option>
      {locations.data?.map((l) => (
        <option key={l.LocationId} value={l.LocationId}>
          {l.LocationDesc}{l.BranchCode ? ` (${l.BranchCode})` : ''}
        </option>
      ))}
    </select>
  );
}
